import get from 'lodash/get';
import isString from 'lodash/isString';
import uniq from 'lodash/uniq';
import { createSelector } from '@reduxjs/toolkit';
import { getCommon, getState } from '@yme/selectors/common';
import { DELIVERY_TEXT, RETURN_TEXT, WARRANTY_TEXT } from '@yme/selectors/constants';
import { commonSelector, detailsPageSelector } from '@yme/selectors/index';
import { getMotorcycleVariationsStatus, getProductDetails, getProductDetailsVariantsWithGroupingProducts, getProductVariantsByColorWithGroupingProducts, } from '@yme/selectors/products';
import { getDescriptionAttribute, getPriceAttribute, getTitleAttribute } from '../helpers';
import { getTechHighlightsParameters } from './helpers';
const getProductCardState = createSelector(getState, (state) => state.productCard);
export const getProductCardLabels = createSelector(getProductCardState, (state) => state.labels);
export const getProductFilterOptions = createSelector(getProductCardState, (state) => state.filterOptions);
export const getProductCurrentSku = createSelector(getProductFilterOptions, (state) => state.sku);
export const getView360State = createSelector(getProductCardState, (state) => state?.is360ViewOpen ?? false);
export const getIsNotifyMeLoaded = createSelector(getProductCardState, (state) => state?.isNotifyMeLoader ?? false);
export const getLinkForTestRide = createSelector(getProductCardState, (state) => state?.linkForTestRide ?? '');
export const getPrevPageType = createSelector(getProductCardState, (state) => state?.prevPageType ?? '');
export const getPrevGroupingProductSku = createSelector(getProductCardState, (state) => state?.prevGroupingProductSku ?? '');
export const getTestRideAvailableLoadingStatus = createSelector(getProductCardState, (state) => state?.isTestRideLinkLoaded ?? false);
export const getCompatibilityCheckAllowed = createSelector(getProductCardState, (state) => state?.isCompatibilityCheckAllowed ?? true);
export const getProductSkuList = createSelector(getProductCardState, (state) => state?.skuList ?? []);
export const getProductQuantity = createSelector(getProductCardState, (state) => state?.productQuantity ?? 0);
export const getSelectedCompatibilityProduct = createSelector(getProductCardState, (state) => state?.selectedCompatibilityProduct);
// General selectors for PDP
const { getAttribute, getMasterData } = detailsPageSelector;
export const getTitleAttributeSelector = createSelector(getMasterData, getTitleAttribute);
const getDescriptionAttributeSelector = createSelector(getMasterData, getDescriptionAttribute);
// TODO: need to get a price by country code
const getPriceAttributeSelector = createSelector(getMasterData, getPriceAttribute);
export const selectProductCardData = createSelector([
    getTitleAttributeSelector,
    getDescriptionAttributeSelector,
    getPriceAttributeSelector,
    commonSelector.getRTL,
], (titleAttribute, descriptionAttribute, priceAttribute, isRightToLeft) => ({
    titleAttribute,
    priceAttribute,
    descriptionAttribute,
    isRightToLeft,
}));
export const getProductBenefits = createSelector(getAttribute(DELIVERY_TEXT), getAttribute(RETURN_TEXT), getAttribute(WARRANTY_TEXT), (deliveryText, returnText, warrantyText) => ({
    deliveryText,
    returnText,
    warrantyText,
}));
// Get unique list of values from all options (Rotation / Size / ControlMethod / StartingMethod)
export const getProductValueByOption = (option) => createSelector([getProductVariantsByColorWithGroupingProducts], (variantsByColor) => {
    const rotationList = variantsByColor &&
        variantsByColor?.reduce((acc, item) => {
            const variantsOfProduct = get(item, ['variantsOfProduct'], []);
            const itemRotation = variantsOfProduct.map((i) => get(i, option));
            if (itemRotation && !acc) {
                return [...itemRotation];
            }
            return [...acc, ...itemRotation];
        }, []);
    return uniq(rotationList);
});
/**
 * getting data for 'TechHighlights' component
 */
export const getTechHighlightsParametersBySku = (sku = '') => createSelector([getProductDetailsVariantsWithGroupingProducts, getCommon], (variants, common) => {
    const itemVariant = variants?.find((item) => item.sku === sku);
    if (itemVariant) {
        return getTechHighlightsParameters({
            itemVariant,
            language: common.language,
        });
    }
    return [];
});
export const getKeyForGroupingProduct = createSelector([
    getProductVariantsByColorWithGroupingProducts,
    getMotorcycleVariationsStatus,
    getProductFilterOptions,
], (variants, isGroupingProduct, filterOptions) => {
    if (!isGroupingProduct || isString(variants)) {
        return '';
    }
    const { sku, colourId } = filterOptions;
    const currentColorVariants = variants.find((variant) => variant.colourId === colourId);
    const currentVariant = currentColorVariants?.variantsOfProduct.find((variant) => variant.sku === sku);
    return currentVariant?.productKey ?? '';
});
export const getProductDetailsWithGroupingProducts = createSelector([getProductDetails, getMotorcycleVariationsStatus, getProductFilterOptions], (product, isGroupingProduct, filterOptions) => {
    const data = product.data;
    if (!filterOptions.sku) {
        return undefined;
    }
    if (!isGroupingProduct) {
        return data;
    }
    const { sku } = filterOptions;
    const variationProduct = data?.groupingProducts?.find((groupingProduct) => {
        return groupingProduct.variants.some((variant) => variant.sku === sku);
    });
    return variationProduct || data;
});
export const getMasterDataWithGroupingProducts = createSelector(getProductDetailsWithGroupingProducts, (product) => product?.variants?.find((item) => item.master));
export function getAttributeWithGroupingProducts(attributeName) {
    return createSelector(getMasterDataWithGroupingProducts, (masterData) => masterData?.attributes.find((attribute) => attribute.name === attributeName)?.value);
}
